import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import { resumeData } from "../data/resumeData";

export default function Experience() {
  const { experience } = resumeData;
  const [selectedJob, setSelectedJob] = useState(null);

  return (
    <section id="experience" className="py-12 px-6 bg-black relative overflow-hidden">
      <div className="container mx-auto relative">
        {/* Title */}
        <motion.h2
          initial={{ y: 20, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-4xl md:text-5xl font-display font-bold text-center mb-20 text-white"
        >
          <span className="bg-gradient-to-r from-green-400 via-primary to-green-400 bg-clip-text text-transparent">
            Professional Journey
          </span>
        </motion.h2>

        {/* Timeline */}
        <div className="max-w-3xl mx-auto relative">
          {/* Timeline Line */}
          <div className="absolute left-4 top-0 bottom-0 w-px bg-primary/20" />

          {experience.map((job, index) => (
            <motion.div
              key={job.company}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.15, duration: 0.5 }}
              className="relative pl-12 pb-12 last:pb-0"
            >
              {/* Dot */}
              <button
                onClick={() => setSelectedJob(job)}
                className={`absolute left-[10px] top-2 w-4 h-4 rounded-full transition-transform focus:outline-none shadow-lg ${selectedJob?.company === job.company
                    ? "bg-primary ring-4 ring-primary/30 scale-110"
                    : "bg-primary hover:scale-110"
                  }`}
                title={`View details for ${job.role}`}
              />

              {/* Summary */}
              <div
                className="bg-zinc-900 border border-zinc-800 p-6 rounded-xl hover:border-primary/40 transition-all cursor-pointer"
                onClick={() => setSelectedJob(job)}
              >
                <h3 className="text-xl font-semibold text-white">{job.role}</h3>
                <p className="text-sm text-primary mb-2">
                  {job.startDate} - {job.endDate}
                </p>
                <p className="text-gray-400">{job.company}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/*Modal */}
        <AnimatePresence>
          {selectedJob && (
            <motion.div
              key="modal"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50"
              onClick={() => setSelectedJob(null)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="bg-zinc-900 border border-gray-700 rounded-2xl p-8 max-w-2xl w-[90%] relative overflow-y-auto max-h-[90vh]"
                onClick={(e) => e.stopPropagation()}
              >
                {/*  Close Button */}
                <button
                  onClick={() => setSelectedJob(null)}
                  className="absolute top-3 right-3 text-gray-400 hover:text-primary text-xl"
                >
                  ✕
                </button>

                {/* Job Info */}
                <h3 className="text-2xl font-bold text-white mb-2">{selectedJob.role}</h3>
                <p className="text-primary mb-1">{selectedJob.company}</p>
                <p className="text-gray-400 mb-4 text-sm">
                  {selectedJob.startDate} – {selectedJob.endDate}
                </p>

                {/* 🔹 Achievements (Top-level) */}
                {selectedJob.achievements && (
                  <>
                    <h4 className="text-lg font-semibold text-white mb-2">
                      Key Achievements
                    </h4>
                    <ul className="space-y-2 mb-6">
                      {selectedJob.achievements.map((a, i) => (
                        <li
                          key={i}
                          className="flex items-start text-gray-300 text-sm leading-relaxed"
                        >
                          <span className="text-primary mr-2">•</span>
                          {a}
                        </li>
                      ))}
                    </ul>
                  </>
                )}

                {/* 🔹 Projects (If available) */}
                {selectedJob.projects && (
                  <>
                    <h4 className="text-lg font-semibold text-white mb-3">
                      Projects
                    </h4>
                    <div className="space-y-6">
                      {selectedJob.projects.map((proj, i) => (
                        <div
                          key={i}
                          className="bg-zinc-800 border border-zinc-700 rounded-xl p-5"
                        >
                          <h5 className="text-md font-semibold text-white">
                            {proj.title}
                          </h5>
                          {proj.period && (
                            <p className="text-xs text-primary mb-2">{proj.period}</p>
                          )}

                          <ul className="space-y-1.5 mb-3">
                            {proj.achievements.map((a, j) => (
                              <li
                                key={j}
                                className="flex items-start text-gray-300 text-sm"
                              >
                                <span className="text-primary mr-2">•</span>
                                {a}
                              </li>
                            ))}
                          </ul>

                          {/* Tech Stack */}
                          <div className="flex flex-wrap gap-2">
                            {proj.technologies.map((tech, t) => (
                              <span
                                key={t}
                                className="text-xs px-3 py-1 bg-primary/10 text-primary rounded-full border border-primary/20"
                              >
                                {tech}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {/* 🔹 Technologies (Top-level, optional) */}
                {selectedJob.technologies && !selectedJob.projects && (
                  <div className="flex flex-wrap gap-2 mt-4">
                    {selectedJob.technologies.map((tech, i) => (
                      <span
                        key={i}
                        className="text-xs px-3 py-1 bg-primary/10 text-primary rounded-full border border-primary/20"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
